import {
  Box,
  Button,
  HStack,
  Text,
  Textarea,
  useColorModeValue,
  IconButton,
  Select,
  Flex,
  Spinner,
} from '@chakra-ui/react';
import axios from 'axios';
import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
  FiBold,
  FiItalic,
  FiUnderline,
  FiLink,
  FiList,
  FiAlignLeft,
  FiAlignCenter,
  FiAlignRight,
} from 'react-icons/fi';
import Card from 'components/card/Card';
import {
  extractFinalBody,
  extractStyles,
  injectStylesOnce,
  sanitizeHtml,
} from '../../../utils/htmlContentUtils';

export default function TermsAndConditions() {
  const [content, setContent] = React.useState('');
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [isEditing, setIsEditing] = React.useState(false);
  const textareaRef = React.useRef(null);

  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const navigate = useNavigate();

  // --- NAYA API CONFIGURATION ---
  const baseUrl = process.env.REACT_APP_BASE_URL;
  const token = localStorage.getItem('token');

  // 1. Fetch Terms (GET)
  const fetchTerms = React.useCallback(async () => {
    try {
      setLoading(true);
      const apiUrl = `${baseUrl}api/admin/terms/terms-conditions`;
      if (!baseUrl || !token) {
        throw new Error('Missing base URL or authentication token');
      }
      const response = await axios.get(apiUrl, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data?.status) {
        const receivedData = response.data.data
          ? response.data.data.content
          : response.data.content;
        const cleanedContent = extractFinalBody(receivedData);
        setContent(cleanedContent || '');

        const styles = extractStyles(receivedData, '.terms-html-root');
        injectStylesOnce(styles, 'terms-html-styles');
      }
      setLoading(false);
    } catch (err) {
      console.error('Fetch Error:', err);
      if (err.response?.status === 401) {
        localStorage.removeItem('token');
        navigate('/');
      } else {
        setError(err.message || 'Failed to fetch content');
        setLoading(false);
      }
    }
  }, [baseUrl, token, navigate]);

  React.useEffect(() => {
    fetchTerms();
  }, [fetchTerms]);

  // 2. Save Terms (POST)
  const handleSave = async () => {
    try {
      const apiUrl = `${baseUrl}api/admin/terms/terms-conditions`;
      const response = await axios.post(
        apiUrl,
        { content },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      if (response.data.status) {
        toast.success('Terms and Conditions updated successfully!', {
          position: 'top-right',
          autoClose: 3000,
        });
        setIsEditing(false);
        fetchTerms(); // Data refresh karne ke liye
      }
    } catch (err) {
      console.error('Update Error:', err);
      toast.error('Failed to update content', {
        position: 'top-right',
        autoClose: 3000,
      });
    }
  };

  // 3. Formatting Logic (Same UI logic)
  const applyFormatting = (format) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = content.slice(start, end);

    if (!selectedText) {
      toast.warn('Please select text to format');
      return;
    }

    let newText = '';
    switch (format) {
      case 'bold':
        newText = `<b>${selectedText}</b>`;
        break;
      case 'italic':
        newText = `<i>${selectedText}</i>`;
        break;
      case 'underline':
        newText = `<u>${selectedText}</u>`;
        break;
      case 'heading1':
        newText = `<h1>${selectedText}</h1>`;
        break;
      case 'heading2':
        newText = `<h2>${selectedText}</h2>`;
        break;
      default:
        newText = selectedText;
    }

    const updatedContent =
      content.slice(0, start) + newText + content.slice(end);
    setContent(updatedContent);

    setTimeout(() => {
      textarea.selectionStart = start;
      textarea.selectionEnd = start + newText.length;
      textarea.focus();
    }, 0);
  };

  if (loading) {
    return (
      <Flex justify="center" align="center" h="100vh" mt="100px">
        <Spinner size="xl" />
      </Flex>
    );
  }

  if (error) {
    return (
      <Card mt="100px" p="25px">
        <Text color="red.500">Error: {error}</Text>
      </Card>
    );
  }

  return (
    <Card
      flexDirection="column"
      w="100%"
      px="0px"
      mt="100px"
      overflowX={{ sm: 'scroll', lg: 'hidden' }}
    >
      <Box px="25px" mb="20px">
        <Text color={textColor} fontSize="22px" fontWeight="700" mb="20px">
          Terms And Conditions
        </Text>

        {isEditing ? (
          <Box>
            <HStack spacing={2} mb={2}>
              <Select
                placeholder="Normal"
                size="sm"
                w="120px"
                onChange={(e) => {
                  if (e.target.value === 'Heading 1')
                    applyFormatting('heading1');
                  if (e.target.value === 'Heading 2')
                    applyFormatting('heading2');
                }}
              >
                <option value="Heading 1">Heading 1</option>
                <option value="Heading 2">Heading 2</option>
                <option value="Normal">Normal</option>
              </Select>
              <IconButton
                icon={<FiBold />}
                size="sm"
                aria-label="Bold"
                onClick={() => applyFormatting('bold')}
              />
              <IconButton
                icon={<FiItalic />}
                size="sm"
                aria-label="Italic"
                onClick={() => applyFormatting('italic')}
              />
              <IconButton
                icon={<FiUnderline />}
                size="sm"
                aria-label="Underline"
                onClick={() => applyFormatting('underline')}
              />
              <IconButton icon={<FiLink />} size="sm" aria-label="Link" />
              <IconButton icon={<FiList />} size="sm" aria-label="List" />
              <IconButton
                icon={<FiAlignLeft />}
                size="sm"
                aria-label="Align Left"
              />
              <IconButton
                icon={<FiAlignCenter />}
                size="sm"
                aria-label="Align Center"
              />
              <IconButton
                icon={<FiAlignRight />}
                size="sm"
                aria-label="Align Right"
              />
            </HStack>
            <Textarea
              ref={textareaRef}
              value={content}
              onChange={(e) => setContent(e.target.value)}
              minH="300px"
              placeholder="Enter Terms And Conditions content here..."
              mb={4}
              bg="white"
              color="black"
              borderColor="gray.300"
              _placeholder={{ color: 'gray.400' }}
              _focus={{
                borderColor: 'brand.500',
                boxShadow: '0 0 0 1px #3182ce',
              }}
            />
            <HStack spacing={3}>
              <Button colorScheme="blue" onClick={handleSave}>
                Save
              </Button>
              <Button variant="outline" onClick={() => setIsEditing(false)}>
                Cancel
              </Button>
            </HStack>
          </Box>
        ) : (
          <Box
            className="terms-html-root"
            bg="white"
            color="black"
            p={4}
            borderRadius="16px"
            border="1px solid"
            borderColor="gray.200"
          >
            <Box
              fontSize="md"
              lineHeight="1.8"
              mb={4}
              color="black"
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(content) }}
            />
            <Button colorScheme="teal" onClick={() => setIsEditing(true)}>
              Edit Terms And Conditions
            </Button>
          </Box>
        )}
      </Box>
      <ToastContainer theme="colored" />
    </Card>
  );
}
